import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getApplicationDetails } from '../common/getApplicationDetails';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { DraftCheck } from 'src/types/PZPermit/pzPermit.type';
import PdfGenerator from 'src/ui-component/PdfGenerator';
import { useDispatch, useSelector } from 'react-redux';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import { httpRequest } from 'src/services/httpRequest';
import { RootState } from 'src/redux-store/appStore';
import LinearStepper from 'src/ui-component/Stepper';
import { sendNotification } from 'src/utils/sendNotification';
import Content from '../Solicitor/Content';
import GeneralInformationForm from './GeneralInformationForm';
import ApplicationDetailsForm from './ApplicationDetailsForm';
import ApplicationFee from 'src/ui-component/ApplicationFee';
import ViewApplication from 'src/ui-component/ViewApplication';
import { FieldsSchema } from './schema';
import ApplicationStatus from 'src/ui-component/ApplicationStatus';
import toast from 'react-hot-toast';
import {
  resetFormData,
  updateFormData
} from 'src/redux-store/slices/constructionFormSlice';
import {
  PaymentApplicationDetails,
  PaymentSummary
} from 'src/pages/Payment/types';
import UploadDocumentsForm from './UploadDocumentsForm';
import TechnicalSections from './TechnicalSections';
import {
  ApplicationStatus1,
  getButtonsConfig,
  UserRole
} from './utils/permitButtonConfig';
import { checkUCCFormStatus } from './utils/uccFormUtils';
import { CertificationTerms } from './CertificationInLieuOfOath';

const EditApplication: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [taxRate, setTaxRate] = useState<number>(0);
  const [switchTo, setSwitchTo] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [getAllData, setGetAllData] = useState<any>(null);
  const { townshipId, permit, applicationID } = useParams();
  const isDraft = location?.state && (location?.state as DraftCheck)?.isDraft;
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const socket = useSelector((store: RootState) => store.socket.socket);
  const currentPermitRole = useSelector(
    (state: RootState) =>
      state?.currentPermit?.currentPermitDetails?.currentPermitUserRole
  );
  const isManager = currentPermitRole === 'Manager';
  const isFDM = currentPermitRole === 'Front Desk Manager';
  const [feeDetails, setFeeDetails] = useState({
    applicationFee: 0,
    inspectionFee: 0
  });
  const formData = useSelector(
    (state: RootState) => state.constructionFormSlice.formData
  );

  // Create refs for each form
  const formRefs = {
    generalInfo: useRef(null),
    propertyDetails: useRef(null),
    uploadDocuments: useRef(null)
  };

  const handleGetApplicationDetails = async () => {
    const response = await getApplicationDetails({
      applicationID,
      permit,
      isDraft
    });
    if (response?.status === 'success') {
      const data = response?.data?.data[0];
      setGetAllData(data);
      dispatch(
        updateFormData({
          ApplicationTitle: data?.ApplicationTitle || '',
          ApplicantName: data?.ApplicantName || '',
          ApplicantType: data?.ApplicantType || '',
          ApplicationStatus: data?.ApplicationStatus || '',
          // ApplicationType: data?.ApplicationType || '',
          PurposeType: data?.PurposeType || '',
          PropertyUseType: data?.PropertyUseType || '',
          PropertyType: data?.PropertyType || '',
          ProjectContactName: data?.ProjectContactName || '',
          ProjectContactPhone: data?.ProjectContactPhone || '',
          BlockLot: data?.BlockLot || '',
          ProjectLocation: data?.ProjectLocation || '',
          AuthorizedEmail: data?.AuthorizedEmail || '',
          ConstructionSchedule: data?.ConstructionSchedule || '',
          DescribeUse: data?.DescribeUse || '',
          OwnersName: data?.OwnersName || '',
          OwnerMobileNumber: data?.OwnerMobileNumber || '',
          AgentsLicenseNumber: data?.AgentsLicenseNumber || '',
          CompanyName: data?.CompanyName || '',
          ContractorLicenseNumber: data?.ContractorLicenseNumber || '',
          ArchitectLicenseNumber: data?.ArchitectLicenseNumber || '',
          TenantName: data?.TenantName || '',
          TenantEmail: data?.TenantEmail || '',
          TenantPhone: data?.TenantPhone || '',
          NumberOfUnits: data?.NumberOfUnits || '',
          NumberOfBedrooms: data?.NumberOfBedrooms || '',
          BusinessOccupancyClassification:
            data?.BusinessOccupancyClassification,
          ParkingRequirements: data?.ParkingRequirements || '',
          IndustrialEquipmentSpecifications:
            data?.IndustrialEquipmentSpecifications,
          AgriculturalEquipmentSpecifications:
            data?.AgriculturalEquipmentSpecifications,
          LandUseAndCropPlan: data?.LandUseAndCropPlan || '',
          ResidentialUnitBreakdown: data?.ResidentialUnitBreakdown || '',
          CommercialUnitBreakdown: data?.CommercialUnitBreakdown || '',
          SharedFacilityManagementPlan:
            data?.SharedFacilityManagementPlan || '',
          PrivatePropertyUsePlan: data?.PrivatePropertyUsePlan || '',
          PropertyTaxInformation: data?.PropertyTaxInformation || '',
          CorporateEntityName: data?.CorporateEntityName || '',
          BusinessRegistrationNumber: data?.BusinessRegistrationNumber || '',
          AuthorizedRepresentative: data?.AuthorizedRepresentative || '',
          CorporateTaxInformation: data?.CorporateTaxInformation || '',
          GovernmentAgencyName: data?.GovernmentAgencyName || '',
          PartnerNames: data?.PartnerNames || '',
          RegisteredPartnershipNumber: data?.RegisteredPartnershipNumber || '',
          PartnershipTaxInformation: data?.PartnershipTaxInformation || '',
          TrusteeName: data?.TrusteeName || '',
          BeneficiariesInformation: data?.BeneficiariesInformation || '',
          EstateTaxInformation: data?.EstateTaxInformatio || '',
          UserID: data?.UserID,
          ApplicationNumber: data?.ApplicationNumber,
          ProposedGroupType: data?.ProposedGroupType,
          PresentGroupType: data?.PresentGroupType,
          LieuAuthCondition: data?.LieuAuthCondition
        })
      );
    }
    if (isDraft) dispatch(updateFormData({ ['DocumentFileUpload']: [] }));
  };

  useEffect(() => {
    handleGetApplicationDetails();
    return () => {
      dispatch(resetFormData());
    };
  }, []);

  const getCurrentFormRef = () => {
    switch (activeStep) {
      case 0:
        return formRefs.generalInfo;
      case 1:
        return formRefs.propertyDetails;
      case 2:
        return formRefs.uploadDocuments;
      default:
        return null;
    }
  };

  const handleStepSubmit = async () => {
    const currentFormRef = getCurrentFormRef();
    if (currentFormRef?.current) {
      try {
        await currentFormRef.current.submitForm();
        const errors = await currentFormRef.current.validateForm();
        return Object.keys(errors).length === 0;
      } catch (error) {
        console.error('Form validation error:', error);
        return false;
      }
    }
    return true; // If no form ref (e.g., Summary step), allow proceeding
  };

  const handleUpdateApplication = async (data: any, type = '') => {
    setLoading(true);
    const response = await httpRequest(
      'PUT',
      `/construction/update/${applicationID}`,
      data
    );
    if (response?.status === 'success') {
      if (type) return true;
      toast.success(`Application Updated successfully.`);
      setLoading(false);
      navigate(
        `/${townshipId}/Building or Construction Department/${permit}/${
          isDraft ? 'draft-application' : 'application-list'
        }`
      );
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  const handleProceedToPay = async (data: any) => {
    const response = await httpRequest(
      'PUT',
      `/construction/update/${applicationID}`,
      data
    );
    if (response?.status === 'success') {
      toast.success('Application submitted successfully.');
      const paymentSummary: PaymentSummary = {
        fees: Object.fromEntries(
          Object.entries(feeDetails).filter(([, value]) => value > 0)
        ),
        ...(taxRate > 0 ? { taxRate } : {})
      };

      const paymentApplicationDetails: PaymentApplicationDetails = {
        applicationDetails: {
          userName: `${userData?.FirstName} ${userData?.LastName}`,
          applicationNumber: null,
          applicationId: applicationID,
          applicationStatus: 'Draft',
          statusPayload: 'In Review L1',
          permitPrefix: 'Construction'
        }
      };
      navigate(`/payment-methods`, {
        state: { paymentSummary, paymentApplicationDetails }
      });
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  const handleApproveApplication = async (data: any) => {
    setLoading(true);
    const payload = {
      ApplicationID: Number(applicationID),
      ApplicationStatus: 'Approved'
      // ProjectLocation: data?.ProjectLocation,
      // ApplicationType: data?.ApplicationType,
      // ProjectContactName: data?.ProjectContactName
    };

    const response = await httpRequest(
      'PUT',
      `/applications/${permit}/update_status`,
      payload
    );
    if (response?.status === 'success') {
      sendNotification(socket, {
        UserID: getAllData?.UserID,
        TownshipID: townshipId,
        ApplicationID: applicationID,
        PermitID: permit,
        Title: 'Application Approved',
        Message: `Construction - Application no. ${data?.ApplicationNumber} is approved.`,
        ApplicationNumber: data?.ApplicationNumber
      });
      toast?.success(`Application Approved successfully.`);
      navigate(
        `/${townshipId}/Building or Construction Department/${permit}/application-list`
      );
    } else {
      toast?.error(response?.message);
      dispatch(handleError(response?.message));
    }
    setLoading(false);
  };

  // You would also need to implement a handleUpdateApplicationStatus function
  const handleUpdateApplicationStatus = async (data, newStatus) => {
    setLoading(true);
    try {
      const response = await httpRequest(
        'PUT',
        `/applications/9/update_status`,
        {
          ApplicationID: applicationID,
          ApplicationStatus: newStatus
        }
      );

      if (response?.status === 'success') {
        toast.success(`Application status updated to ${newStatus}`);
        navigate(
          `/${townshipId}/Building or Construction Department/${permit}/${
            isDraft ? 'draft-application' : 'application-list'
          }`
        );
        // Refresh data if needed
      } else {
        toast.error('Failed to update application status');
      }
    } catch (error) {
      console.error('Error updating application status:', error);
      toast.error('Failed to update application status');
    }finally{
      setLoading(false)
    }
  };

  // Add these state variables to your component
  const [isChecking, setIsChecking] = useState(false);
  const [pendingForms, setPendingForms] = useState([]);
  const [showPendingFormsDialog, setShowPendingFormsDialog] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleTermsAccepted = (accepted: boolean) => {
    setTermsAccepted(accepted);
  };

  // Now update the handleButtonAction function to use this utility
  const handleButtonAction = async (type: string) => {
    switch (type) {
      case 'Next':
        // eslint-disable-next-line no-case-declarations
        const isValid = await handleStepSubmit();
        if (isValid) {
          setActiveStep((prev) => prev + 1);
        }
        break;

      case 'Back':
        setActiveStep((prev) => prev - 1);
        break;

      case 'Proceed To Payment':
        // Update the button state to show checking status
        setIsChecking(true);
        {
          // Check UCC form status
          const paymentStatusCheck = await checkUCCFormStatus(applicationID);

          if (paymentStatusCheck.success) {
            if (paymentStatusCheck.allFormsCompleted) {
              // All good, proceed with payment
              handleProceedToPay(formData);
            } else {
              // Show dialog with pending forms
              setPendingForms(paymentStatusCheck.pendingForms);
              setShowPendingFormsDialog(true);
            }
          } else {
            // Show error message
            toast.error(paymentStatusCheck.error);
          }
        }
        setIsChecking(false);
        break;

      case 'Update':
        handleUpdateApplication(formData);
        break;

      case 'Send For L1 Review':
        // No UCC form check required for L1 review
        // handleUpdateApplicationStatus(formData, 'In Review L1');
        // break;

        setIsChecking(true);
        {
          // Check UCC form status
          const paymentStatusCheck = await checkUCCFormStatus(applicationID);

          if (paymentStatusCheck.success) {
            if (paymentStatusCheck.allFormsCompleted) {
              // All good, proceed with payment
              handleUpdateApplicationStatus(formData, 'In Review L1');
            } else {
              // Show dialog with pending forms
              setPendingForms(paymentStatusCheck.pendingForms);
              setShowPendingFormsDialog(true);
            }
          } else {
            // Show error message
            toast.error(paymentStatusCheck.error);
          }
        }
        setIsChecking(false);
        break;

      case 'Send For L2 Review':
        // Check UCC form status before sending to L2
        setIsChecking(true);
        {
          const l2StatusCheck = await checkUCCFormStatus(applicationID);

          if (l2StatusCheck.success) {
            if (l2StatusCheck.allFormsCompleted) {
              // All forms complete, send to L2
              handleUpdateApplicationStatus(formData, 'In Review L2');
            } else {
              // Show dialog with pending forms
              setPendingForms(l2StatusCheck.pendingForms);
              setShowPendingFormsDialog(true);
              toast.error(
                'All UCC forms must be completed before sending to L2 Review'
              );
            }
          } else {
            toast.error(l2StatusCheck.error);
          }
        }
        setIsChecking(false);
        break;

      case 'Send For Revision':
        handleUpdateApplicationStatus(formData, 'Revision');
        break;

      case 'Approve':
        // Check UCC form status before approval
        setIsChecking(true);
        {
          const approvalStatusCheck = await checkUCCFormStatus(applicationID);

          if (approvalStatusCheck.success) {
            if (approvalStatusCheck.allFormsCompleted) {
              // All forms complete, can approve
              await handleUpdateApplication(formData, 'Approve');
              handleApproveApplication(formData);
            } else {
              // Show dialog with pending forms
              setPendingForms(approvalStatusCheck.pendingForms);
              setShowPendingFormsDialog(true);
              toast.error('All UCC forms must be completed before approval');
            }
          } else {
            toast.error(approvalStatusCheck.error);
          }
        }
        setIsChecking(false);
        break;

      default:
        break;
    }
  };

  const steps = [
    {
      label: 'General Information',
      Component: (
        <>
          <GeneralInformationForm
            ref={formRefs.generalInfo}
            formData={formData}
            handleSubmit={() => {}}
            isEdit={false}
          />
          <ApplicationFee
            setTaxRate={setTaxRate}
            setFeeDetails={setFeeDetails}
            feeDetails={feeDetails}
          />

          <Content
            switchTo={switchTo}
            setSwitchTo={setSwitchTo}
            applicationID={applicationID}
            UserID={userData?.UserID}
            notificationReceiverUserID={getAllData?.UserID}
            ApplicationStatus={formData?.ApplicationStatus}
            ApplicationNumber={formData?.ApplicationNumber}
            selectMultipleFiles={false}
            uploadDocsHide={isDraft}
          />
        </>
      )
    },
    {
      label: 'Property Details',
      Component: (
        <ApplicationDetailsForm
          ref={formRefs.propertyDetails}
          formData={formData}
          handleSubmit={() => {}}
        />
      )
    },
    {
      label: 'Technical Sections',
      Component: <TechnicalSections applicationId={Number(applicationID)} />
    },
    {
      label: 'Upload Documents',
      Component: <UploadDocumentsForm formData={formData} />
    },
    {
      label: 'Summary',
      Component: (
        <>
          <ViewApplication StaticDetails={formData} />
          <Box sx={{ margin: 'auto', maxWidth: '1400px', p: { md: 3, xs: 1 } }}>
            <CertificationTerms
              role={formData?.ApplicantType}
              onAccept={handleTermsAccepted}
            />
          </Box>
        </>
      )
    }
  ];

  // Then modify your finalStepButtons function
  function finalStepButtons() {
    // Get current application status
    const applicationStatus = getAllData?.ApplicationStatus || 'Draft';

    // Determine user role
    let userRole: UserRole = 'User'; // Default role
    if (isManager) {
      userRole = 'Manager';
    } else if (isFDM) {
      userRole = 'Front Desk Manager';
    }

    // Get the button configuration based on status and role
    const buttonConfigs = getButtonsConfig(
      applicationStatus as ApplicationStatus1,
      userRole,
      isChecking
    );

    // Convert button configs to the format expected by your component
    return buttonConfigs.map((config) => ({
      label: config.label,
      function: handleButtonAction,
      disabled: config.disabled || !termsAccepted
    }));
  }

  return loading ? (
    <LoadingOverlay />
  ) : (
    <Container sx={{ mt: 2 }} className="main-table-container">
      <Breadcrumb PermitName={'Construction'} PageTitle={'New Application'} />

      <FormHeaderGreeting
        name={`${userData?.FirstName} ${userData?.LastName}`}
        applicationType={'edit'}
        applicationStatus={getAllData?.ApplicationStatus}
        applicationNumber={getAllData?.ApplicationNumber}
      />

      <Card>
        <Box
          m={3}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {getAllData && getAllData?.ApplicationStatus !== 'Draft' ? (
            <ApplicationStatus editAppStatus={getAllData} />
          ) : (
            <Typography variant="h4">Edit Application</Typography>
          )}
          <PdfGenerator
            PermitName={'Construction'}
            allFields={FieldsSchema(formData)}
          />
        </Box>

        <Box m={3}>
          {switchTo ? (
            <Content
              switchTo={switchTo}
              setSwitchTo={setSwitchTo}
              applicationID={applicationID}
              UserID={userData?.UserID}
              ApplicationStatus={getAllData?.ApplicationStatus}
              ApplicationNumber={getAllData?.ApplicationNumber}
              selectMultipleFiles={true}
            />
          ) : (
            <>
              <LinearStepper
                activeStep={activeStep}
                steps={steps}
                stepperButton={[
                  {
                    label: 'Back',
                    function: handleButtonAction
                  },
                  {
                    label: 'Next',
                    function: handleButtonAction
                  }
                ]}
                finalStepButtons={finalStepButtons()}
              />
              {/* Add the dialog to show pending forms */}
              <Dialog
                open={showPendingFormsDialog}
                onClose={() => setShowPendingFormsDialog(false)}
                maxWidth="md"
                fullWidth
              >
                <DialogTitle>
                  <Typography variant="h4" color="error">
                    Cannot Proceed to Payment
                  </Typography>
                </DialogTitle>
                <Divider />
                <DialogContent>
                  <Box sx={{ mb: 2 }}>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      You must wait until all shared forms are completed before
                      proceeding to payment. You can view the status of each
                      form in the UCC Forms section.
                    </Alert>
                    <br />
                    <Typography variant="body1">
                      You have pending UCC forms that need to be completed
                      before proceeding to payment:
                    </Typography>
                  </Box>

                  {pendingForms.map((form, index) => (
                    <Box
                      key={index}
                      sx={{ mb: 3, p: 2, bgcolor: '#fff4e5', borderRadius: 1 }}
                    >
                      <Typography fontWeight="bold">
                        {form.formType} Form - {form.pendingCount} pending
                      </Typography>

                      <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2">
                          Pending for:
                        </Typography>
                        <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                          {form.items.map((item, idx) => (
                            <li key={idx}>
                              <Typography variant="body2">
                                {item.ProfessionalName} (
                                {item.ProfessionalEmail})
                              </Typography>
                            </li>
                          ))}
                        </ul>
                      </Box>
                    </Box>
                  ))}
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => setShowPendingFormsDialog(false)}
                    variant="contained"
                  >
                    OK, I'll Check Later
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </Box>
      </Card>
    </Container>
  );
};

export default EditApplication;
